import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import tw from 'twin.macro'
import { useDispatch, useSelector } from 'react-redux'
import { FetchContractorsCounts, FetchSubcontractorCounts } from 'src/constants/actionTypes'
import { getSelectedMarket } from 'src/selectors/market'
import { Typography } from '@mui/material'
import { ContractorState } from 'src/constants/contractor/enums'
import { ManageTagsButton } from 'src/components/tags/ManageTagsButton'
import { ContractorTabs } from '../tabs/ContractorTabs'
import { useLocation, useNavigate } from 'react-router-dom'
import { ContractorsTable } from '../contractors-table/ContractorsTable'
import { ContractorsContext, IContractorsContext } from './Contractors.context'
import { TabsSelector } from 'src/components/shared/tabs-selector/TabsSelector'
import { getSubcontractorCounts } from 'src/selectors/contractor'
import { SubcontractorCounts } from 'src/interfaces/contractor'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { getAccessInfo } from 'src/selectors/user'
import { UserProductName } from 'src/constants/user'
import { contractorListHeaderTitles } from 'src/constants/contractor/constants'

const ContractorsComponent = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const { state } = location

  const selectedMarket = useSelector(getSelectedMarket)
  const subcontractorCounts = useSelector(getSubcontractorCounts)
  const accessInfo = useSelector(getAccessInfo)

  const selectedTabRef = useRef<ContractorState | null>(null)

  const [selectedSubcontractorId, setSelectedSubcontractorId] = useState<string>(
    state?.masterContractorRelationId || null,
  )
  const [selectedTab, setSelectedTab] = useState(ContractorState.Active)
  const [searchQuery, setSearchQuery] = useState<string>('')

  const handleChangeTab = useCallback((tab: ContractorState) => {
    selectedTabRef.current = tab
    setSelectedTab(tab)
  }, [])

  useEffect(() => {
    if (!state?.selectedTab || state?.selectedTab === selectedTabRef.current) {
      return
    }

    handleChangeTab(state?.selectedTab)
  }, [state?.selectedTab])

  useEffect(() => {
    if (state?.masterContractorId) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [state?.masterContractorId])

  const fetchContractorsCounts = useCallback(() => {
    const params: any = {}

    if (selectedMarket?.id) {
      params.partnerMarketId = selectedMarket?.id
    }

    dispatch(FetchContractorsCounts.request(params))
  }, [selectedMarket?.id])

  useEffect(() => {
    fetchContractorsCounts()
  }, [fetchContractorsCounts])

  useEffect(() => {
    if (!accessInfo?.products?.some((item) => item.product === UserProductName.Subcontractor)) {
      return
    }

    const fetchSubcontractorCounts = async () => {
      const data: SubcontractorCounts = await createAsyncAction(
        dispatch,
        FetchSubcontractorCounts.request(),
      )

      if (!selectedSubcontractorId && data.length) {
        setSelectedSubcontractorId(data[0].id || '')
      }
    }

    fetchSubcontractorCounts()
  }, [selectedSubcontractorId])

  const contractorsContextValue = useMemo<IContractorsContext>(() => {
    return {
      searchQuery,
      setSearchQuery,
    }
  }, [searchQuery, setSearchQuery])

  const tabOptions = useMemo(() => {
    if (!subcontractorCounts) {
      return []
    }

    return subcontractorCounts.map((item) => {
      return {
        label: contractorListHeaderTitles[item.id] || item.name,
        value: item.id,
        count: item.count,
      }
    })
  }, [subcontractorCounts])

  return (
    <ContractorsContext.Provider value={contractorsContextValue}>
      <div>
        <div css={tw`flex flex-row items-center justify-between mb-6 gap-4`}>
          <Typography variant="h2" css={tw`mb-2`}>
            Contractors List
          </Typography>
          {accessInfo?.products?.some((item) => item.product === UserProductName.Subcontractor) && (
            <TabsSelector
              menuLabel="Select master contractor"
              selectedValue={selectedSubcontractorId}
              onSelect={setSelectedSubcontractorId}
              options={tabOptions}
            />
          )}
          <div>
            <ManageTagsButton />
          </div>
        </div>
        <ContractorTabs selectedTab={selectedTab} onClick={handleChangeTab} />
        {selectedTab === ContractorState.Active && (
          <ContractorsTable
            selectedState={ContractorState.Active}
            selectedSubcontractorId={selectedSubcontractorId}
          />
        )}
        {selectedTab === ContractorState.Deactivated && (
          <ContractorsTable
            selectedState={ContractorState.Deactivated}
            selectedSubcontractorId={selectedSubcontractorId}
          />
        )}
        {selectedTab === ContractorState.Recollection && (
          <ContractorsTable
            selectedState={ContractorState.Recollection}
            selectedSubcontractorId={selectedSubcontractorId}
          />
        )}
      </div>
    </ContractorsContext.Provider>
  )
}

export const Contractors = memo(ContractorsComponent)
